import studentService from 'ApiService/Admin/studentService';
import Card from 'components/card';
import CardMenu from 'components/card/CardMenu';
import { AuthContext } from 'contexts/AuthContext';
import Nodatafound from 'pages/Common/Nodatafound';
import { useContext, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { ThreeDots } from 'react-loader-spinner';
import StudentDetails from '../../StudentDetails/Index';

const AllStudents = ({ classData, selectedBranch }) => {
  const { state } = useContext(AuthContext);
  const [studentDetails, setStudentDetails] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [showDetails, setShowDetails] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingPagination, setLoadingPagination] = useState(false);
  const [filteredData, setFilteredData] = useState([]);
  const [filter, setFilter] = useState('all');
  const [sortOrder, setSortOrder] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [studentDetailsLoading, setStudentDetailsLoading] = useState(false);

  async function fetchStudents() {
    setLoading(true);
    try {
      const response = await studentService.getAllStudentDetailsData(
        classData.courseId,
        classData.batchId,
        selectedBranch
      );
      if (response.data.success) {
        setStudentDetails(response.data.data);
        setLoading(false);
        if (response.data.length === 0) {
          toast.error(
            'No students found for the selected Standard and Division.'
          );
        }
      }
    } catch (error) {
      setLoading(false);
      toast.error(error?.response?.data?.message);
    }
  }

  const fetchStudentDetails = async (studentId) => {
    setStudentDetailsLoading(true);
    try {
      const response = await studentService.getStudentDetailsById(
        studentId._id
      );
      if (response.data.success) {
        setSelectedStudent(response.data.data);
        setShowDetails(true);
      } else {
        toast.error('Failed to fetch student details');
      }
    } catch (error) {
      toast.error(
        error?.response?.data?.message || 'Error fetching student details'
      );
    } finally {
      setStudentDetailsLoading(false);
    }
  };

  useEffect(() => {
    fetchStudents();
  }, [selectedBranch, classData, currentPage, itemsPerPage]);

  useEffect(() => {
    let result = [...studentDetails];

    // Apply search filter
    if (searchTerm) {
      result = result.filter(
        (student) =>
          student.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          student.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
          (student.contactNumber &&
            student.contactNumber
              .toLowerCase()
              .includes(searchTerm.toLowerCase()))
      );
    }

    // Apply gender filter
    if (filter !== 'all') {
      result = result.filter(
        (student) => student.gender.toLowerCase() === filter
      );
    }

    // Apply sorting
    if (sortOrder) {
      result.sort((a, b) => {
        const compareValue = a.name.localeCompare(b.name);
        return sortOrder === 'asc' ? compareValue : -compareValue;
      });
    }

    setFilteredData(result);
  }, [studentDetails, filter, sortOrder, searchTerm]);

  function resetFilters() {
    setFilteredData(studentDetails);
    setSearchTerm('');
    setFilter('all');
    setSortOrder('');
  }

  const handlePagination = async (page) => {
    setLoadingPagination(true);
    setCurrentPage(page);
    setLoadingPagination(false);
  };

  const handleAttendanceClick = (studentId) => {
    fetchStudentDetails(studentId);
  };

  const handleBackClick = () => {
    setSelectedStudent(null);
    setShowDetails(false);

    fetchStudents();
  };

  return (
    <div className="flex h-full items-center justify-center">
      {loading ? (
        <div className="flex h-40 items-center justify-center">
          <ThreeDots color="#5431ed" className="mt-100" />
        </div>
      ) : (
        <>
          {showDetails ? (
            <StudentDetails
              selectedStudent={selectedStudent}
              handleBackClick={handleBackClick}
              studentDetailsLoading={studentDetailsLoading}
              selectedBranch={selectedBranch}
              fetchStudents={fetchStudents}
            />
          ) : (
            <Card extra="w-full sm:overflow-auto p-4">
              <div className="mt-2 flex w-full items-center justify-end px-1">
                <CardMenu setItemsPerPage={setItemsPerPage} />
              </div>
              <div className="mt-2 flex flex-col items-center gap-4 sm:flex-row">
                <div className="w-full rounded-xl border border-gray-200 bg-lightPrimary py-2.5 pl-2.5 pr-3 text-sm font-normal text-gray-600 outline-none dark:!border-white/10 dark:!bg-navy-800 dark:text-white sm:w-full md:w-full">
                  <input
                    type="text"
                    placeholder="Search by Name, Email, or Contact"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="block w-full rounded-full bg-lightPrimary text-base text-navy-700 outline-none dark:!bg-navy-800 dark:text-white"
                  />
                </div>

                <div className="w-full sm:w-[300px]">
                  <div className="w-full rounded-xl border border-gray-200 py-3 pl-2.5 pr-3 text-sm font-normal text-gray-600 outline-none dark:!border-white/10 dark:!bg-navy-800 dark:text-white sm:w-full md:w-full">
                    <select
                      className="text-lightFourth w-full rounded-xl pr-4 text-sm outline-none dark:!bg-navy-800 dark:text-white"
                      value={filter}
                      onChange={(e) => setFilter(e.target.value)}
                    >
                      <option value="all"> Gender</option>
                      <option value="male">Male</option>
                      <option value="female">Female</option>
                    </select>
                  </div>
                </div>
                <div className="w-full sm:w-[300px]">
                  <div className="w-full rounded-xl border border-gray-200 py-3 pl-2.5 pr-3 text-sm font-normal text-gray-600 outline-none dark:!border-white/10 dark:!bg-navy-800 dark:text-white sm:w-full md:w-full">
                    <select
                      className="text-lightFourth w-full rounded-xl pr-4 text-sm outline-none dark:!bg-navy-800 dark:text-white"
                      value={sortOrder}
                      onChange={(e) => setSortOrder(e.target.value)}
                    >
                      <option value=""> Order</option>
                      <option value="asc">Ascending</option>
                      <option value="desc">Descending</option>
                    </select>
                  </div>
                </div>
                <div className="w-full sm:w-[200px]">
                  <button
                    onClick={resetFilters}
                    className="w-40 rounded-xl bg-brand-500 py-2.5 pl-2 pr-3 text-base font-medium text-white transition duration-200 hover:bg-brand-600 active:bg-brand-700 dark:bg-brand-400 dark:text-white dark:hover:bg-brand-300 dark:active:bg-brand-200"
                  >
                    Reset
                  </button>
                </div>
              </div>
              <div className="mt-8 overflow-x-scroll xl:overflow-x-hidden">
                {filteredData.length === 0 ? (
                  <div className="mt-4 text-center text-gray-600 dark:text-white">
                    <p className="mt-0 text-center ">
                      {' '}
                      <Nodatafound title="No Student data found" />
                    </p>
                  </div>
                ) : (
                  <table
                    className="w-full"
                    variant="simple"
                    color="gray-500"
                    mb="24px"
                  >
                    <thead>
                      <tr>
                        <th className="border-b border-gray-200 pb-2 pl-4 pr-4 text-center text-sm font-bold tracking-wide text-gray-600 dark:text-white lg:text-xs">
                          Sr No
                        </th>
                        <th className="border-b border-gray-200 pb-2 pl-4 pr-4 text-center text-sm font-bold tracking-wide text-gray-600 dark:text-white lg:text-xs">
                          Name
                        </th>
                        <th className="border-b border-gray-200 pb-2 pl-4 pr-4 text-center text-sm font-bold tracking-wide text-gray-600 dark:text-white lg:text-xs">
                          Gender
                        </th>
                        <th className="border-b border-gray-200 pb-2 pl-4 pr-4 text-center text-sm font-bold tracking-wide text-gray-600 dark:text-white lg:text-xs">
                          Birth Date
                        </th>
                        <th className="border-b border-gray-200 pb-2 pl-4 pr-4 text-center text-sm font-bold tracking-wide text-gray-600 dark:text-white lg:text-xs">
                          Email
                        </th>
                        <th className="border-b border-gray-200 pb-2 pl-4 pr-4 text-center text-sm font-bold tracking-wide text-gray-600 dark:text-white lg:text-xs">
                          Contact Number
                        </th>
                        <th className="border-b border-gray-200 pb-2 pl-4 pr-4 text-center text-sm font-bold tracking-wide text-gray-600 dark:text-white lg:text-xs">
                          Address
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredData.map((student, index) => (
                        <tr
                          key={student._id}
                          onClick={() => handleAttendanceClick(student)}
                          className="cursor-pointer"
                        >
                          <td className="pb-2 pl-4 pr-4 pt-2 text-center text-sm font-bold text-navy-700 dark:text-white">
                            {index + 1}
                          </td>
                          <td className="pb-2 pl-4 pr-4 pt-2 text-center text-sm font-bold text-navy-700 dark:text-white">
                            {student.name}
                          </td>
                          <td className="pb-2 pl-4 pr-4 pt-2 text-center text-sm font-bold text-navy-700 dark:text-white">
                            {student.gender}
                          </td>
                          <td className="pb-2 pl-4 pr-4 pt-2 text-center text-sm font-bold text-navy-700 dark:text-white">
                            {new Date(student.dateOfBirth).toLocaleDateString()}
                          </td>
                          <td className="pb-2 pl-4 pr-4 pt-2 text-center text-sm font-bold text-navy-700 dark:text-white">
                            {student.email}
                          </td>
                          <td className="pb-2 pl-4 pr-4 pt-2 text-center text-sm font-bold text-navy-700 dark:text-white">
                            {student.contactNumber}
                          </td>
                          <td className="pb-2 pl-4 pr-4 pt-2 text-center text-sm font-bold text-navy-700 dark:text-white">
                            {student.address.street}, {student.address.city},{' '}
                            {student.address.state},{' '}
                            {student.address.postalCode},{' '}
                            {student.address.country}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
                {/* Pagination */}
                <div className="mt-[20px] flex justify-center px-4 px-[27px] py-[16px]">
                  {/* Button to navigate to previous page */}
                  <button
                    onClick={() => handlePagination(currentPage - 1)}
                    disabled={currentPage === 1 || loadingPagination}
                    className="h-10 w-10 rounded-full bg-brand-500 text-lg text-white transition duration-200 hover:bg-brand-600 active:bg-brand-700 dark:bg-brand-400 dark:text-white dark:hover:bg-brand-300 dark:active:bg-brand-200"
                  >
                    &lt;
                  </button>
                  {/* Display current page */}
                  <div className="text-black flex h-10 w-10 items-center  justify-center text-lg transition duration-200  active:bg-brand-700  dark:text-white  dark:active:bg-brand-200">
                    {currentPage}
                  </div>
                  {/* Button to navigate to next page */}
                  <button
                    onClick={() => handlePagination(currentPage + 1)}
                    disabled={currentPage === totalPages || loadingPagination}
                    className="h-10 w-10 rounded-full bg-brand-500 text-lg text-white transition duration-200 hover:bg-brand-600 active:bg-brand-700 dark:bg-brand-400 dark:text-white dark:hover:bg-brand-300 dark:active:bg-brand-200"
                  >
                    &gt;
                  </button>
                </div>
              </div>
            </Card>
          )}
        </>
      )}
    </div>
  );
};

export default AllStudents;
